import React, { useCallback, useRef, useState } from 'react';
import { FieldErrors, SubmitHandler } from 'react-hook-form';
import {
  ISportBackground,
  ISportBackgroundFormData,
} from '../../../../interfaces/user-sport-background.interface';
import StepForm from '../StepForm';
import OnboardingSportBackgroundForm, {
  FormRef,
  OnboardingSportBackgroundFormInputs,
} from './components/OnboardingSportBackgroundForm';

interface OnboardingSportBackgroundStepProps {
  onSubmitSport: SubmitHandler<ISportBackgroundFormData>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  step: number;
  isLoading: boolean;
  savedSportBackgrounds?: ISportBackground[];
  onInvalid?: () => void;
}

const OnboardingSportBackgroundStep = ({
  onSubmitSport,
  setStep,
  step,
  isLoading,
  savedSportBackgrounds,
  onInvalid,
}: OnboardingSportBackgroundStepProps) => {
  const formRef = useRef<FormRef | null>(null);
  const [isFormTouched, setIsFormTouched] = useState(false);

  const scrollToFirstError = (
    errors: FieldErrors<OnboardingSportBackgroundFormInputs>
  ) => {
    const findFirstPath = (obj: any, parentPath = ''): string | null => {
      if (!obj || typeof obj !== 'object') return null;

      if (obj.message && typeof obj.message === 'string') {
        return parentPath;
      }

      for (const key of Object.keys(obj)) {
        const nextPath = parentPath ? `${parentPath}.${key}` : key;
        const found = findFirstPath(obj[key], nextPath);
        if (found) return found;
      }

      return null;
    };

    const firstPath = findFirstPath(errors);
    if (!firstPath) return;

    const el = document.querySelector(
      `[name="${CSS.escape(firstPath)}"]`
    ) as HTMLElement | null;

    if (!el) return;

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof (el as any).focus === 'function') {
      (el as any).focus();
    }
  };

  const onNext = () => {
    const handleValidation = formRef?.current?.handleSubmit;

    if (!handleValidation) return;

    const executeSubmission = handleValidation(
      (data: OnboardingSportBackgroundFormInputs) => {
        onSubmitSport({ sportBackgrounds: data.sportBackgrounds });
      },
      (errors) => {
        scrollToFirstError(errors);
        onInvalid?.();
      }
    );
    executeSubmission();
  };

  const handleFormTouchChange = useCallback((isTouched: boolean) => {
    setIsFormTouched(isTouched);
  }, []);

  const isNextEnabled = !isLoading && isFormTouched;

  return (
    <StepForm
      title="Sport background"
      onNext={onNext}
      nextDisabled={!isNextEnabled}
      backDisabled={isLoading}
      step={step}
      setStep={setStep}
    >
      <OnboardingSportBackgroundForm
        ref={formRef}
        savedSportBackgrounds={savedSportBackgrounds}
        onFormTouchChange={handleFormTouchChange}
      />
    </StepForm>
  );
};

export default OnboardingSportBackgroundStep;
